import React, { useCallback, useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import IngramTab from "./IngramTab";
import MatchingTab from "./MatchingTab";
import GoogleTab from "./GoogleTab";
import ReconTab from "./ReconTab";
import SkuMappingTab, { prefillFromSku } from "./SkuMappingTab";
import { StatusChip } from "./RunPanel";
import { zb, KIND_LABELS, when } from "./api";
import styles from "./ZohoBilling.module.css";

const TABS = [
  { key: "ingram", label: "Ingram statement" },
  { key: "matching", label: "Payments & credits" },
  { key: "google", label: "Google bills" },
  { key: "recon", label: "Reconciliation" },
  { key: "sku", label: "SKU mapping" },
  { key: "history", label: "History" },
];
const TAB_OF_KIND = { ingram: "ingram", credits: "matching", payments: "matching", google: "google", recon: "recon" };

function ConfigWarnings({ config }) {
  if (!config) return null;
  const items = [];
  if (config.zohoMissing.length) {
    items.push(`Zoho Books is not configured on the server (missing ${config.zohoMissing.join(", ")}). Nothing can be previewed or posted.`);
  }
  if (config.ingramError) {
    items.push(`Ingram portal login is not configured (${config.ingramError}). Invoices that aren't downloaded yet can't be fetched.`);
  }
  if (!config.browserInstalled) {
    items.push("The Playwright browser is not installed on the server (run `npx playwright install --with-deps chromium` in admin_backend). Invoice downloads will fail.");
  }
  if (!items.length) return null;
  return (
    <div className={styles.errorBox}>
      {items.map((t) => (
        <div key={t}>{t}</div>
      ))}
    </div>
  );
}

function History({ runs }) {
  if (!runs.length) return <div className={styles.card}>No jobs yet.</div>;
  return (
    <div className={styles.card}>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>#</th>
              <th>Job</th>
              <th>Status</th>
              <th>Input</th>
              <th>Started by</th>
              <th>Created</th>
              <th>Posted</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id}>
                <td>{r.id}</td>
                <td>{KIND_LABELS[r.kind] || r.kind}</td>
                <td>
                  <StatusChip status={r.status} />
                </td>
                <td className={styles.small}>
                  {r.input?.files?.length ? `${r.input.files[0].name}${r.input.files.length > 1 ? ` +${r.input.files.length - 1}` : ""}` : "—"}
                </td>
                <td className={styles.small}>{r.created_by || "—"}</td>
                <td className={styles.small}>{when(r.createdAt)}</td>
                <td className={styles.small}>{r.posted_at ? `${when(r.posted_at)} · ${r.posted_by || ""}` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const ZohoBilling = () => {
  const [tab, setTab] = useState(() => {
    try {
      return localStorage.getItem("zohoBilling.tab") || "ingram";
    } catch {
      return "ingram";
    }
  });
  const [status, setStatus] = useState(null);
  const [skuPrefill, setSkuPrefill] = useState(null);

  const refresh = useCallback(async () => {
    try {
      setStatus(await zb("/status"));
    } catch (err) {
      toast.error(err.message);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // While a job runs (possibly started by someone else), keep the banner and
  // the disabled buttons honest.
  useEffect(() => {
    if (!status?.activeRunId) return undefined;
    const t = setInterval(refresh, 10000);
    return () => clearInterval(t);
  }, [status?.activeRunId, refresh]);

  const pickTab = (key) => {
    setTab(key);
    try {
      localStorage.setItem("zohoBilling.tab", key);
    } catch {
      /* per-viewer convenience only */
    }
  };

  const onMapSku = useCallback((sku) => {
    setSkuPrefill(prefillFromSku(sku));
    pickTab("sku");
  }, []);
  const clearPrefill = useCallback(() => setSkuPrefill(null), []);

  const runs = status?.runs || [];
  const active = status?.activeRunId ? runs.find((r) => r.id === status.activeRunId) : null;
  const shared = { runs, activeRunId: status?.activeRunId || null, onChanged: refresh };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <h1>Zoho Billing</h1>
          <div className={styles.subtitle}>
            Ingram and Google invoices into Zoho Books as vendor bills. Every step previews first; nothing is posted until
            you confirm.
          </div>
        </div>
      </div>

      {!status ? (
        <CircularProgress size={24} />
      ) : (
        <>
          <ConfigWarnings config={status.config} />
          {active && (
            <div className={styles.infoBox}>
              <CircularProgress size={12} sx={{ mr: 1, verticalAlign: "middle" }} />
              {KIND_LABELS[active.kind]} #{active.id} is running (started by {active.created_by}). Other jobs wait until it
              finishes.{" "}
              <button type="button" className={styles.linkBtn} onClick={() => pickTab(TAB_OF_KIND[active.kind])}>
                Open it
              </button>
            </div>
          )}

          <div className={styles.chipRow}>
            {TABS.map((t) => (
              <span
                key={t.key}
                role="button"
                tabIndex={0}
                className={`${styles.chip} ${tab === t.key ? styles.chipActive : ""}`}
                onClick={() => pickTab(t.key)}
                onKeyDown={(e) => e.key === "Enter" && pickTab(t.key)}
              >
                {t.label}
              </span>
            ))}
          </div>

          {tab === "ingram" && <IngramTab {...shared} onMapSku={onMapSku} />}
          {tab === "matching" && <MatchingTab {...shared} />}
          {tab === "google" && <GoogleTab {...shared} />}
          {tab === "recon" && <ReconTab {...shared} />}
          {tab === "sku" && <SkuMappingTab prefill={skuPrefill} onPrefillUsed={clearPrefill} />}
          {tab === "history" && <History runs={runs} />}
        </>
      )}
    </div>
  );
};

export default ZohoBilling;
